import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Activity,
  AlertTriangle,
  Battery,
  Crosshair,
  Fuel,
  LogOut,
  MapPin,
  Navigation,
  Radio,
  Route,
  ShieldAlert,
  Ship,
  Waves,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import operatorDashboardApi from '@/services/operatorDashboardApi';
import { cn } from '@/lib/utils';
import FleetMap from '@/components/map/FleetMap';

const SOCKET_PATH = '/api/socket.io';
const SEVERITY_RANK = { critical: 5, high: 4, medium: 3, warning: 3, low: 2, info: 1 };

const AlertBanner = ({ alerts = [], equipment }) => {
  const topAlert = useMemo(() => {
    return [...alerts].sort((a, b) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0))[0];
  }, [alerts]);

  const severity = topAlert?.severity || 'normal';
  const critical = severity === 'critical' || severity === 'high';
  const warning = severity === 'medium' || severity === 'warning';

  return (
    <header
      className={cn(
        'flex h-[72px] shrink-0 items-center justify-between border-b px-5',
        critical && 'border-red-400/70 bg-red-700 text-white operator-critical-blink',
        warning && 'border-amber-300/70 bg-amber-600 text-white',
        !critical && !warning && 'border-border bg-background text-foreground'
      )}
    >
      <div className="flex min-w-0 items-center gap-4">
        {critical ? <ShieldAlert className="h-9 w-9 shrink-0" /> : <Ship className="h-8 w-8 shrink-0 text-primary" />}
        <div className="min-w-0">
          <div className="truncate text-2xl font-semibold leading-tight">
            {topAlert ? topAlert.message : `${equipment?.name || 'Equipamento'} em monitoramento`}
          </div>
          <div className="truncate text-sm opacity-75">
            {equipment?.tag || equipment?.id || 'Sem TAG'} - {equipment?.status === 'online' ? 'ONLINE' : 'OFFLINE'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-right">
        <div className="text-sm uppercase tracking-wide opacity-70">{severity === 'normal' ? 'NORMAL' : severity}</div>
        <AlertTriangle className={cn('h-7 w-7', !topAlert && 'opacity-30')} />
      </div>
    </header>
  );
};

const MetricRow = ({ metric, icon: Icon = Activity }) => (
  <div className={cn('rounded-md border border-border bg-card p-3', metric?.stale && 'opacity-60')}>
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4 shrink-0 text-primary" />
        <span className="truncate text-sm">{metric?.label || 'Metrica'}</span>
      </div>
      {metric?.alert_zone?.severity && (
        <span className="rounded bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-500">
          {metric.alert_zone.severity}
        </span>
      )}
    </div>
    <div className="mt-2 truncate text-3xl font-semibold text-foreground">
      {metric?.formatted || '-'}
    </div>
  </div>
);

const StatusPill = ({ children, tone = 'slate' }) => (
  <span className={cn(
    'rounded-full px-2.5 py-1 text-xs font-medium uppercase',
    tone === 'green' && 'bg-green-500/20 text-green-500',
    tone === 'red' && 'bg-red-500/20 text-red-500',
    tone === 'slate' && 'bg-secondary text-secondary-foreground'
  )}>
    {children}
  </span>
);

const OperatorDashboardPage = () => {
  const { equipmentId } = useParams();
  const { token, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followLocked, setFollowLocked] = useState(true);
  const [trailHours, setTrailHours] = useState('24'); // mesmo default do principal
  const [trail, setTrail] = useState({ points: [] });
  const socketRef = useRef(null);
  // Trava de concorrência: o /live é caro (análise operacional + sources);
  // impede empilhar chamadas (causa raiz do pico de CPU/queda do servidor).
  const liveInFlightRef = useRef(false);
  const trailInFlightRef = useRef(false);

  const loadDashboard = useCallback(async () => {
    setError(null);
    const response = await operatorDashboardApi.getDashboard(equipmentId);
    setDashboard(response.data);
  }, [equipmentId]);

  const loadLive = useCallback(async () => {
    if (liveInFlightRef.current) return; // não empilha /live (endpoint pesado)
    liveInFlightRef.current = true;
    try {
      const response = await operatorDashboardApi.getLive(equipmentId);
      setDashboard((current) => ({
        ...(current || {}),
        ...response.data,
        trail: current?.trail || response.data.trail,
      }));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Falha ao atualizar painel');
    } finally {
      liveInFlightRef.current = false;
    }
  }, [equipmentId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadDashboard()
      .catch((err) => {
        if (active) setError(err?.response?.data?.detail || 'Falha ao carregar painel');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadDashboard]);

  useEffect(() => {
    const interval = setInterval(loadLive, 30000);
    return () => clearInterval(interval);
  }, [loadLive]);

  // Trilha: mesmo endpoint/filtro do dashboard principal. Fetch iniciado pelo
  // usuário (troca de período) + refresh lento (60s) com trava — NÃO é socket-driven,
  // então não reintroduz a tempestade de requests.
  const loadTrail = useCallback(async () => {
    if (trailInFlightRef.current) return;
    trailInFlightRef.current = true;
    try {
      const response = await operatorDashboardApi.getTrail(equipmentId, parseInt(trailHours, 10));
      setTrail({ points: response.data?.points || [] });
    } catch (err) {
      // trilha é não-crítica para o painel; preserva a anterior em caso de falha
    } finally {
      trailInFlightRef.current = false;
    }
  }, [equipmentId, trailHours]);

  useEffect(() => {
    loadTrail();
    const interval = setInterval(loadTrail, 60000);
    return () => clearInterval(interval);
  }, [loadTrail]);

  useEffect(() => {
    if (!token) return undefined;
    socketRef.current = io(process.env.REACT_APP_BACKEND_URL, {
      path: SOCKET_PATH,
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('equipment_update', (equipment) => {
      if (equipment?.id !== equipmentId) return;
      setDashboard((current) => current ? {
        ...current,
        equipment: {
          ...current.equipment,
          status: equipment.status,
          latitude: equipment.latitude,
          longitude: equipment.longitude,
          last_seen: equipment.last_seen,
          // heading/cog/speed_knots já vêm convertidos no broadcast (fix CC-2026-05-29-001);
          // só atualiza quando presentes para não apagar o último valor conhecido.
          heading: equipment.heading != null ? equipment.heading : current.equipment?.heading,
          cog: equipment.cog != null ? equipment.cog : current.equipment?.cog,
          speed_knots: equipment.speed_knots != null ? equipment.speed_knots : current.equipment?.speed_knots,
        },
      } : current);
    });

    // Atualiza a lista de alertas IN-PLACE a partir do payload do socket — sem
    // recarregar o /live caro a cada alerta (era a tempestade que saturava o
    // servidor). Bônus: alerta resolvido some do banner reativamente.
    const upsertAlert = (alert) => {
      if (!alert || alert.equipment_id !== equipmentId) return;
      setDashboard((current) => {
        if (!current) return current;
        const alerts = Array.isArray(current.alerts) ? current.alerts : [];
        const without = alerts.filter((a) => a.id !== alert.id);
        const next = alert.status && alert.status !== 'active'
          ? without                 // resolvido/inativo → remove
          : [alert, ...without];    // novo/atualizado ativo → topo
        return { ...current, alerts: next };
      });
    };
    socketRef.current.on('new_alert', upsertAlert);
    socketRef.current.on('alert_updated', upsertAlert);

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [equipmentId, token]);

  const equipment = dashboard?.equipment;
  const position = equipment?.latitude != null && equipment?.longitude != null
    ? [equipment.latitude, equipment.longitude]
    : null;
  // Equipamento focado, no formato do FleetMap (ícone heading/COG vem das props).
  const focusEquipments = useMemo(() => {
    if (!equipment || equipment.latitude == null || equipment.longitude == null) return [];
    return [{
      id: equipmentId,
      name: equipment.name,
      tag: equipment.tag,
      latitude: equipment.latitude,
      longitude: equipment.longitude,
      heading: equipment.heading,
      cog: equipment.cog,
      speed_knots: equipment.speed_knots,
      marker_color: equipment.marker_color || (equipment.status === 'online' ? 'green' : 'gray'),
      status: equipment.status,
      equipment_type_name: equipment.equipment_type_name,
    }];
  }, [equipment, equipmentId]);
  // Modo seguir do kiosk mapeado para o conjunto do FollowController do FleetMap.
  const followedIds = useMemo(
    () => (followLocked && equipmentId ? new Set([equipmentId]) : new Set()),
    [followLocked, equipmentId],
  );
  const currentSegment = dashboard?.operations?.current_segment;
  const operationsSummary = dashboard?.operations?.summary;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-8 text-foreground">
        <div className="max-w-md rounded-md border border-destructive/50 bg-destructive/10 p-5">
          <div className="text-xl font-semibold">Painel indisponivel</div>
          <div className="mt-2 text-sm text-muted-foreground">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-y-auto bg-background text-foreground lg:h-screen lg:min-h-[720px] lg:overflow-hidden">
      <AlertBanner alerts={dashboard?.alerts || []} equipment={equipment} />

      <main className="grid min-h-0 flex-1 grid-cols-1 gap-3 p-3 lg:grid-cols-[260px_minmax(0,1fr)_280px]">
        <aside className="flex min-h-0 flex-col gap-3 overflow-hidden">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm uppercase tracking-wide text-muted-foreground">Viagem</div>
              <StatusPill tone={dashboard?.operations?.available ? 'green' : 'slate'}>
                {dashboard?.operations?.available ? 'ativa' : 'sem dados'}
              </StatusPill>
            </div>
            <div className="mt-3 rounded-md bg-muted/50 p-3">
              <div className="text-sm text-muted-foreground">Regime atual</div>
              <div className="mt-1 truncate text-2xl font-semibold" style={{ color: currentSegment?.color || 'hsl(var(--foreground))' }}>
                {currentSegment?.regime_name || 'Indefinido'}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded bg-muted/50 p-2">
                <div className="text-muted-foreground">Consumo</div>
                <div className="text-lg font-semibold">{operationsSummary?.total_fuel_liters ?? 0} L</div>
              </div>
              <div className="rounded bg-muted/50 p-2">
                <div className="text-muted-foreground">Distancia</div>
                <div className="text-lg font-semibold">{operationsSummary?.total_distance_km ?? 0} km</div>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            {(dashboard?.widgets?.left || []).map((metric, index) => (
              <MetricRow
                key={`${metric.path || metric.label}-${index}`}
                metric={metric}
                icon={metric.path?.includes('fuel') || metric.label?.includes('Consumo') ? Fuel : Navigation}
              />
            ))}
          </div>
        </aside>

        <section className="relative min-h-[360px] overflow-hidden rounded-md border border-border bg-card lg:min-h-0">
          {position ? (
            <FleetMap
              equipments={focusEquipments}
              trails={{ [equipmentId]: trail }}
              geofences={dashboard?.geofences || []}
              selectedEquipmentId={equipmentId}
              followedIds={followedIds}
              onFollowDisengage={() => setFollowLocked(false)}
              initialCenter={position}
              initialZoom={15}
              defaultLayer="satellite"
              renderPopup={(eq) => eq.name || eq.tag}
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <MapPin className="mr-2 h-5 w-5" />
              Sem posicao GPS
            </div>
          )}

          {/* Controles espelhando o dashboard principal: período de trajeto + Seguir + Centralizar */}
          <div className="absolute left-3 top-3 z-[500] flex items-center gap-2">
            <Select value={trailHours} onValueChange={setTrailHours}>
              <SelectTrigger className="w-[80px] h-8 text-xs bg-background/90 backdrop-blur">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1h</SelectItem>
                <SelectItem value="4">4h</SelectItem>
                <SelectItem value="12">12h</SelectItem>
                <SelectItem value="24">24h</SelectItem>
                <SelectItem value="48">48h</SelectItem>
                <SelectItem value="72">72h</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant={followLocked ? 'default' : 'secondary'}
              className={cn('gap-1', followLocked && 'bg-primary hover:bg-primary/90')}
              onClick={() => setFollowLocked((value) => !value)}
              title={followLocked ? 'Seguindo o equipamento (arraste o mapa para soltar)' : 'Seguir o equipamento'}
            >
              <Navigation className="h-4 w-4" />
              {followLocked ? 'Seguindo' : 'Seguir'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="gap-1"
              onClick={() => setFollowLocked(true)}
              title="Centralizar no equipamento"
            >
              <Crosshair className="h-4 w-4" />
              Centralizar
            </Button>
          </div>
        </section>

        <aside className="flex min-h-0 flex-col gap-3 overflow-hidden">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm uppercase tracking-wide text-muted-foreground">Sistema</div>
              <Button type="button" variant="ghost" size="icon" title="Sair" onClick={logout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded bg-muted/50 p-2">
                <Radio className="mb-2 h-4 w-4 text-primary" />
                <StatusPill tone={equipment?.status === 'online' ? 'green' : 'red'}>
                  {equipment?.status === 'online' ? 'online' : 'offline'}
                </StatusPill>
              </div>
              <div className="rounded bg-muted/50 p-2">
                <Route className="mb-2 h-4 w-4 text-primary" />
                <div className="text-lg font-semibold">{trail?.points?.length || 0}</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {(dashboard?.widgets?.right || []).map((metric, index) => (
              <MetricRow
                key={`${metric.path || metric.label}-${index}`}
                metric={metric}
                icon={metric.path?.includes('battery') ? Battery : metric.path?.includes('depth') ? Waves : Zap}
              />
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border bg-card p-3">
            <div className="mb-3 text-sm uppercase tracking-wide text-muted-foreground">Zonas e alertas</div>
            <div className="space-y-2">
              {(dashboard?.current_geofences || []).map((zone) => (
                <div key={zone.geofence_id} className="rounded bg-muted/50 p-2 text-sm">
                  <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: zone.color || '#38bdf8' }} />
                  {zone.geofence_name}
                </div>
              ))}
              {!(dashboard?.current_geofences || []).length && (
                <div className="rounded bg-muted/50 p-2 text-sm text-muted-foreground">Fora de zonas</div>
              )}
              {(dashboard?.alerts || []).slice(0, 5).map((alert) => (
                <div key={alert.id} className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm">
                  <div className="font-medium text-destructive">{alert.severity}</div>
                  <div className="mt-1 text-muted-foreground">{alert.message}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default OperatorDashboardPage;
